import {useState} from "react";
import AddIcon from "@mui/icons-material/Add";
import {useQueryClient} from "react-query";
import {useTranslation} from "react-i18next";
import {
    Reservation,
    ReservationStatus
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import {
    useCurrentApp,
    useCurrentAppId
} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {useSector} from "crqlar-react-common/dist/utils/hooks/sector/useSectors";
import {
    useService
} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import {
    addMinutes,
    attachCurrentTimeToOtherDate,
    isSameDay
} from "crqlar-react-common/dist/utils/date.util";
import {useReservationsState} from "../../../../../store/reservations.reducer";
import ReservationDialog from "../../../reservationDialog/ReservationDialog";
import {useFloorplanContext} from "../hooks/useFloorplanContext";
import TableOverlayActionButton from "./TableOverlayActionButton";

interface Props {
    resource?: Resource;
}

const AddReservationButton: React.FC<Props> = ({resource}) => {
    const {t} = useTranslation();
    const {restaurantId} = useFloorplanContext();
    const appId = useCurrentAppId();
    const appData = useCurrentApp();
    const sector = useSector(appData?.configuration?.sectorId);
    const defaultService = useService(sector?.defaultServiceId);
    const {fromDate} = useReservationsState(appId);
    const queryClient = useQueryClient();

    const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);

    const handleClick = () => {
        const now = new Date();
        const startDate = isSameDay(now, fromDate)
            ? now
            : attachCurrentTimeToOtherDate(fromDate);

        setSelectedReservation({
            resourceIds: resource?.id ? [restaurantId, resource.id] : [restaurantId],
            expectedStartDate: startDate,
            expectedEndDate: addMinutes(startDate, defaultService?.durationMinutes || 60),
            status: ReservationStatus.CONFIRMED,
            sectorId: sector?.id,
            serviceId: defaultService?.id,
        });
    };

    return (
        <>
            <TableOverlayActionButton
                label={t("Add")}
                onClick={handleClick}
            >
                <AddIcon sx={{fontSize: 18}}/>
            </TableOverlayActionButton>

            <ReservationDialog
                open={!!selectedReservation}
                onClose={() => setSelectedReservation(null)}
                reservation={selectedReservation ?? undefined}
                onSave={() => {
                    setSelectedReservation(null);
                    queryClient.invalidateQueries({queryKey: ["floorplanAssigned"]});
                    queryClient.invalidateQueries({queryKey: ["floorplanUnassigned"]});
                }}
                onChangeReservation={setSelectedReservation}
            />
        </>
    );
};

export default AddReservationButton;
